import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  Stack,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faQuestionCircle,
  faSnowflake,
  faBolt,
  faBuilding,
  faFaucet,
  faHeadset,
  faCalendarAlt,
  faShieldAlt,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { faqData } from "../../data/index";
import { Helmet } from "react-helmet-async";

export default function FAQ() {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const categories = [
    { id: "all", label: "Todas as Categorias", icon: faQuestionCircle },
    { id: "climatizacao", label: "Climatização", icon: faSnowflake },
    { id: "eletrica", label: "Elétrica", icon: faBolt },
    { id: "construcao", label: "Construção Civil", icon: faBuilding },
    { id: "hidraulica", label: "Hidráulica", icon: faFaucet },
    { id: "geral", label: "Geral", icon: faHeadset },
  ];

  const filteredFAQs =
    selectedCategory === "all"
      ? faqData
      : faqData.filter((faq) => faq.category === selectedCategory);

  const getCategoryIcon = (categoryId: string) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.icon : faQuestionCircle;
  };

  return (
    <>
      <Helmet>
        <title>Perguntas Frequentes | Clima Tropical Angola</title>

        <meta
          name="description"
          content="Encontre respostas para as perguntas mais frequentes sobre climatização, manutenção de ar condicionado, construção civil, orçamentos, assistência técnica e serviços especializados da Clima Tropical em Angola."
        />

        <meta
          name="keywords"
          content="FAQ Clima Tropical, perguntas frequentes climatização, manutenção de ar condicionado Angola, assistência técnica Luanda, orçamento climatização, serviços técnicos Angola"
        />

        <link rel="canonical" href="https://climatropical.com/faq" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Perguntas Frequentes | Clima Tropical Angola"
        />

        <meta
          property="og:description"
          content="Tire suas dúvidas sobre os serviços da Clima Tropical, incluindo climatização, manutenção, construção civil e assistência técnica em Angola."
        />

        <meta property="og:url" content="https://climatropical.com/faq" />

        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta
          name="twitter:title"
          content="Perguntas Frequentes | Clima Tropical Angola"
        />

        <meta
          name="twitter:description"
          content="Veja respostas rápidas sobre serviços, manutenção, orçamentos e suporte técnico da Clima Tropical."
        />
      </Helmet>

      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          // py: { xs: 4, md: 8 },
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(130deg, ${theme.palette.primary.main} 30%, ${theme.palette.info.main} 170%)`,
            color: "white",
            py: { xs: 6, md: 10 },
            mb: 8,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", maxWidth: "800px", mx: "auto" }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                }}
              >
                Perguntas Frequentes
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                }}
              >
                Encontre respostas para as dúvidas mais comuns sobre nossos
                serviços, orçamentos, prazos e muito mais.
              </Typography>
              <Chip
                label={`${faqData.length} perguntas respondidas`}
                variant="filled"
                sx={{
                  backgroundColor: "white",
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                  fontSize: "1rem",
                  py: 1,
                }}
              />
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Category Filter */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mb: 4,
                textAlign: "center",
              }}
            >
              Escolha uma Categoria
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "center",
              }}
            >
              {categories.map((category) => (
                <Chip
                  key={category.id}
                  icon={<FontAwesomeIcon icon={category.icon} />}
                  label={category.label}
                  onClick={() => setSelectedCategory(category.id)}
                  variant={
                    selectedCategory === category.id ? "filled" : "outlined"
                  }
                  color="primary"
                  sx={{
                    py: 2,
                    px: 2,
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    ...(selectedCategory === category.id && {
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                    }),
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* FAQ Accordions */}
          <Box sx={{ mb: 8 }}>
            <Stack spacing={2}>
              {filteredFAQs.map((faq, index) => (
                <Accordion
                  key={index}
                  expanded={expanded === `panel${index}`}
                  onChange={handleChange(`panel${index}`)}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px !important",
                    boxShadow: theme.shadows[1],
                    "&:before": { display: "none" },
                    "&:hover": {
                      boxShadow: theme.shadows[3],
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
                    sx={{
                      backgroundColor:
                        expanded === `panel${index}`
                          ? theme.palette.primary.light
                          : theme.palette.background.paper,
                      color:
                        expanded === `panel${index}`
                          ? "white"
                          : theme.palette.text.primary,
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor:
                          expanded === `panel${index}`
                            ? theme.palette.primary.main
                            : theme.palette.action.hover,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FontAwesomeIcon
                        icon={getCategoryIcon(faq.category)}
                        style={{
                          color:
                            expanded === `panel${index}`
                              ? "white"
                              : theme.palette.primary.main,
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {faq.question}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: theme.palette.background.default,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.7,
                        fontSize: "1rem",
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Box>

          {/* CTA Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "center",
              justifyContent: "space-between",
              p: { xs: 4, md: 6 },
              background: `linear-gradient(130deg, ${theme.palette.primary.main} 30%, ${theme.palette.info.main} 170%)`,
              color: "white",
              borderRadius: 4,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Não encontrou sua resposta?
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                Nossa equipe está pronta para esclarecer todas as suas dúvidas
                pessoalmente.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <FontAwesomeIcon icon={faHeadset} />
                <Typography variant="body2">
                  Atendimento personalizado em até 24 horas
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "white",
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                  px: 4,
                  py: 1.5,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  },
                }}
                href="/fale-conosco"
              >
                Fale Conosco
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  display: "flex",
                  gap: 1,
                  borderColor: "white",
                  color: "white",
                  fontWeight: "bold",
                  px: 3,
                  py: 1,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
                href="tel:+244222678777"
              >
                <FontAwesomeIcon icon={faPhone} /> Ligar Agora
              </Button>
            </Box>
          </Box>

          {/* Quick Info Cards */}
          <Box sx={{ mt: 6 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
              }}
            >
              <Paper
                sx={{
                  p: 3,
                  flex: 1,
                  textAlign: "center",
                  border: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <FontAwesomeIcon
                  icon={faShieldAlt}
                  size="2x"
                  color={theme.palette.primary.main}
                  style={{ marginBottom: 16 }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Garantia Total
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  12 meses de garantia em todos os serviços de climatização
                </Typography>
              </Paper>

              <Paper
                sx={{
                  p: 3,
                  flex: 1,
                  textAlign: "center",
                  border: `2px solid ${theme.palette.secondary.main}`,
                }}
              >
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  size="2x"
                  color={theme.palette.secondary.main}
                  style={{ marginBottom: 16 }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Atendimento Rápido
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Resposta em até 2 horas e agendamento flexível
                </Typography>
              </Paper>

              <Paper
                sx={{
                  p: 3,
                  flex: 1,
                  textAlign: "center",
                  border: `2px solid ${theme.palette.success.main}`,
                }}
              >
                <FontAwesomeIcon
                  icon={faHeadset}
                  size="2x"
                  color={theme.palette.success.main}
                  style={{ marginBottom: 16 }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Suporte 24/7
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Plantão para emergências em climatização e elétrica
                </Typography>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}
